import React, { useState, useEffect, useContext } from 'react';
import styles from './styles.module.scss';
import { InputWapper, Input, Select, TextArea } from './styles';
import ButtonInput from '../ButtonInput';
import { Formik, Form } from 'formik';
import { checkoutCartSchema } from '../../utils/validationSchema';
import FormikPhoneInputElement from '../PhoneInput';
import AppContext from '../../hooks/appContext';
import Endpoint from '../../utils/endpoint';
import doToast from '../doToast/doToast';
import { calcCartDiscount, calcCartTax, calcCartTotal, formatMoney } from '@/modules/storefront/utils/helpers';
import { isEmpty } from 'lodash';
import TextInput from '@/modules/storefront/components/TextInput';
import SelectInput from '@/modules/link/components/Select';
import { log } from 'next/dist/server/typescript/utils';

const ShippingForm = (props) => {
  const [noDeliverySettings, setNoDeliverySettings] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [merchantId, setMerchantId] = useState('');
  const [couponIsLoading, setCouponIsLoading] = useState(false);

  const {
    shippingFormValues,
    setShippingFormValues,
    onlyDigitalProducts,
    deliverySettings,
    pickupLocations,
    setDeliverySettings,
    selectDeliveryLocation,
    selectPickupLocation,
    setPaymentError,
    setCoupon,
    currentCurrency,
    deliveryFee,
    getStoreTax,
    cart,
    coupon,
    discountAmt,
    setDiscountAmt,
    changeCartPage,
    validateShippingForm,
    activeAttributes,
  } = useContext(AppContext);

  useEffect(() => {
    if (coupon) {
      let discountAmount = calcCartDiscount(
        cart,
        deliveryFee,
        coupon?.value,
        coupon?.type,
        currentCurrency,
        activeAttributes
      );
      setDiscountAmt(discountAmount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coupon]);

  useEffect(() => {
    !deliverySettings?.settings?.pickup_location && deliverySettings?.locations?.count === 0
      ? setNoDeliverySettings(true)
      : setNoDeliverySettings(false);
  }, [deliverySettings]);

  const getMerchantId = () => {
    let fullUrl = window.location.href;
    let n = fullUrl.lastIndexOf('/');
    let merchantId = fullUrl.substring(n + 1);
    setMerchantId(merchantId);
    return merchantId;
  };

  const getDeliveryLocations = () => {
    let merchantId = getMerchantId();
    Endpoint.getLocations(merchantId)
      .then((res) => {
        setDeliverySettings(res.data.data);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const applyCoupon = async () => {
    let merchantId = getMerchantId();
    if (shippingFormValues.couponCode) {
      setCouponIsLoading(true);
      try {
        const res = await Endpoint.getDiscountByCode(merchantId, shippingFormValues.couponCode);
        setCouponIsLoading(false);

        if (res.data.status === 200) {
          doToast('Coupon applied!', 'success');
          setCoupon(res.data.data);
        } else {
          doToast('Discount not found or expired', 'error');
          setCoupon(null);
        }
      } catch (error) {
        setCouponIsLoading(false);
        doToast('Discount not found or expired', 'error');
        setCoupon(null);
      }
    } else {
      setPaymentError('Please enter a valid coupon code');
      setTimeout(() => {
        setPaymentError('');
      }, 3000);
    }
  };

  useEffect(() => {
    getDeliveryLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeCartPage = (values, setSubmitting) => {
    if (cart.length > 0) {
      changeCartPage('order');
      setShippingFormValues(values);
    }
    setSubmitting(false);
  };

  let cartTax = getStoreTax();
  const taxAmount = calcCartTax(
    cart,
    cartTax,
    deliveryFee,
    coupon?.value,
    coupon?.type,
    currentCurrency,
    activeAttributes
  );

  const isOnlyDigital = onlyDigitalProducts();

  const [deliveryMethodOptions, setDeliveryMethodOptions] = useState([]);
  const [deliveryLocations, setDeliveryLocations] = useState([]);
  const [allPickupLocations, setAllPickupLocations] = useState([]);

  useEffect(() => {
    let optionsList = [];
    if (pickupLocations?.count > 0) {
      let pickupOption = {
        label: 'Pick Up',
        value: 'pickup',
      };
      optionsList.push(pickupOption);
    }

    if (deliverySettings?.locations?.count > 0) {
      let deliveryOption = {
        label: 'Delivery',
        value: 'delivery',
      };
      optionsList.push(deliveryOption);

      const result = deliverySettings?.locations?.rows?.map((location) => ({
        label: location.name,
        value: location.slug,
        cost: location.amounts.find((amount) => amount.currency_id === currentCurrency)?.amount || 0,
      }));
      setDeliveryLocations(result);
    }
    setDeliveryMethodOptions(optionsList);

    if (pickupLocations?.count > 0) {
      const pickLocations = pickupLocations?.rows?.map((location) => ({
        label: location.name,
        value: location.slug,
      }));
      setAllPickupLocations(pickLocations);
    }
  }, []);

  const pickupLocationClasses = `${styles['pickup-location']} ${
    pickupLocations?.count === 0 ? styles['danger-text'] : ''
  }`;
  const noLocationClasses = `${styles['no-location']} ${styles['danger-text']}`;
  const discountClasses = `${styles['fee-value']} ${styles.discount}`;
  const checkoutButtonClasses = `${styles.btn} ${styles['btn-checkout']}`;

  return (
    <>
      <div className={styles['shipping-form']}>
        <Formik
          initialValues={shippingFormValues}
          validationSchema={checkoutCartSchema}
          onSubmit={(values, { setSubmitting }) => handleChangeCartPage(values, setSubmitting)}
        >
          {({ isSubmitting, values, setFieldValue, errors }) => (
            <Form>
              <div className={styles['details-form']}>
                <div className={styles['form-box-inner']}>
                  <TextInput
                    name="customerName"
                    type="text"
                    label="Full Name"
                    placeholder="Enter your first and last name"
                  />
                </div>

                <FormikPhoneInputElement
                  required={true}
                  name="phone"
                  dialcodename="dialCode"
                  dialcodevalue={values.dialCode}
                />

                <div className={styles['form-box-inner']}>
                  <TextInput
                    name="customerEmail"
                    type="email"
                    label="Email address"
                    placeholder="e.g david@gmail.com"
                  />
                </div>
              </div>

              {noDeliverySettings ? null : isOnlyDigital ? null : (
                <SelectInput
                  placeholder="Select Delivery Type"
                  name="deliveryMethod"
                  label="Select Delivery Method"
                  required={true}
                  options={deliveryMethodOptions}
                  onChange={(e) => {
                    setFieldValue('deliveryMethod', e.target.value);
                    if (e.target.value === 'pickup') {
                      selectDeliveryLocation('none');
                      setFieldValue('deliveryLocation', '');
                    }
                  }}
                />
              )}

              <div className={styles['delivery-options']}>
                {values?.deliveryMethod === 'pickup' ? (
                  <SelectInput
                    placeholder="Choose Pickup Location"
                    name="pickupLocation"
                    label="Select Pickup Location"
                    required={values.deliveryMethod === 'pickup'}
                    options={allPickupLocations}
                  />
                ) : null}
                {values?.deliveryMethod === 'delivery' ? (
                  <>
                    {deliverySettings.locations.count > 0 ? (
                      <>
                        <SelectInput
                          placeholder="Select Location"
                          name="deliveryLocation"
                          label="Select Delivery Location"
                          required={values?.deliveryMethod === 'delivery'}
                          options={deliveryLocations}
                          onChange={(e) => {
                            setFieldValue('deliveryLocation', e.target.value);
                            selectDeliveryLocation(e.target.value);
                          }}
                        />

                        {values?.deliveryMethod === 'delivery' && (
                          <>
                            <div className={styles['form-box-inner']}>
                              <TextInput
                                name="customerAddress"
                                type="address"
                                label="Your address"
                                placeholder="Enter your specific address"
                                required={values.deliveryMethod === 'delivery'}
                              />
                            </div>

                            <div className={styles['form-box-inner']}>
                              <TextInput
                                name="notes"
                                type="text"
                                label="Notes"
                                placeholder="Optional notes..."
                                required={false}
                              />
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <div className={noLocationClasses}>No delivery location set by merchant.</div>
                    )}
                  </>
                ) : null}

                <div className={styles['form-box-inner']}>
                  <label>Add Discount/Coupon Code</label>
                  <ButtonInput
                    placeholder="Code"
                    button="Apply"
                    name="couponCode"
                    onChange={(e) => setShippingFormValues({ ...shippingFormValues, couponCode: e.target.value })}
                    checkCode={applyCoupon}
                    isLoading={couponIsLoading}
                  />
                </div>
              </div>

              <div className={styles['cart-summary']}>
                <div className={styles['summary-top']}>
                  <div className={styles['cart-count']}>
                    {cart.length} {cart.length !== 1 ? 'items' : 'item'}
                  </div>
                  <div className={styles['cart-total']}>
                    {currentCurrency === 'NGN' ? <span dangerouslySetInnerHTML={{ __html: '&#8358;' }}></span> : '$'}{' '}
                    {formatMoney(calcCartTotal(cart, currentCurrency) / 100)}
                  </div>
                </div>

                {deliveryFee > 0 && (
                  <div className={styles['delivery-fee']}>
                    <div className={styles['fee-title']}>Delivery fee</div>
                    <div className={styles['fee-value']}>
                      {currentCurrency === 'NGN' ? <span dangerouslySetInnerHTML={{ __html: '&#8358;' }}></span> : '$'}{' '}
                      {formatMoney(deliveryFee / 100)}
                    </div>
                  </div>
                )}

                {cartTax && (
                  <div className={styles['delivery-fee']}>
                    <div className={styles['fee-title']}>Vat</div>
                    <div className={styles['fee-value']}>
                      {currentCurrency === 'NGN' ? <span dangerouslySetInnerHTML={{ __html: '&#8358;' }}></span> : '$'}{' '}
                      {formatMoney(
                        calcCartTax(
                          cart,
                          cartTax,
                          deliveryFee,
                          coupon?.value,
                          coupon?.type,
                          currentCurrency,
                          activeAttributes
                        ) / 100
                      )}
                    </div>
                  </div>
                )}

                {discountAmt > 0 && (
                  <div className={styles['delivery-fee']}>
                    <div className={styles['fee-title']}>Discount</div>
                    <div className={discountClasses}>
                      -{currentCurrency === 'NGN' ? <span dangerouslySetInnerHTML={{ __html: '&#8358;' }}></span> : '$'}{' '}
                      {formatMoney(discountAmt / 100)}
                    </div>
                  </div>
                )}

                <div className={styles['delivery-fee']}>
                  <div className={styles['fee-title']}>Total</div>
                  <div className={styles['fee-value']}>
                    {currentCurrency === 'NGN' ? <span dangerouslySetInnerHTML={{ __html: '&#8358;' }}></span> : '$'}{' '}
                    {formatMoney(
                      (calcCartTotal(cart, currentCurrency) +
                        parseInt(deliveryFee) +
                        parseInt(taxAmount) -
                        parseInt(discountAmt)) /
                        100
                    )}
                  </div>
                </div>

                <div className={styles['summary-bottom']}>
                  <button className={checkoutButtonClasses} type="submit" disabled={!isEmpty(errors)}>
                    Checkout
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default ShippingForm;
